// ──────────────────────────────────────────────────────────────────────
// Trial-limit enforcement library.
//
// Trial scope is the (year_id, subject) pair. Each pair has independent
// counters per mode:
//   • practice — 10 questions cumulative
//   • exam     — 10 questions cumulative
//   • learn    — 1 topic only (the first topic opened)
//
// Counters live in Supabase so a user can't reset by clearing browser
// data. Admin users (a future server-checked role; no client code or URL grants it) bypass all checks at the
// caller level — these helpers never run for admins.
//
// Every function is null-safe: if `supabase` is null (env not configured)
// we return permissive defaults so the app degrades gracefully to "no
// trial limits" rather than locking everyone out.
// ──────────────────────────────────────────────────────────────────────

import { supabase, supabaseEnabled } from './supabase';

export const TRIAL_LIMITS = Object.freeze({
  practice: 10,
  exam:     10,
  learn:    1,
});

// Read the row for (user, year, subject, mode). Returns:
//   { count, topics_opened } if a row exists,
//   { count: 0, topics_opened: [] } otherwise.
export async function getTrialUsage(userId, yearId, subject, mode) {
  if (!supabaseEnabled || !userId) return { count: 0, topics_opened: [] };
  try {
    const { data, error } = await supabase
      .from('trial_usage')
      .select('count, topics_opened')
      .eq('user_id', userId)
      .eq('year_id', yearId)
      .eq('subject', subject)
      .eq('mode', mode)
      .maybeSingle();
    if (error) return { count: 0, topics_opened: [] };
    return data || { count: 0, topics_opened: [] };
  } catch (_) {
    return { count: 0, topics_opened: [] };
  }
}

export async function incrementTrialUsage(userId, yearId, subject, mode, delta = 1) {
  if (!supabaseEnabled || !userId) return;
  try {
    await supabase.rpc('increment_trial_usage', {
      p_user_id: userId,
      p_year_id: yearId,
      p_subject: subject,
      p_mode: mode,
      p_delta: delta,
    });
  } catch (_) { /* swallow — best-effort */ }
}

export async function recordTopicOpened(userId, yearId, subject, topicId) {
  if (!supabaseEnabled || !userId || !topicId) return;
  try {
    await supabase.rpc('append_topic_opened', {
      p_user_id: userId,
      p_year_id: yearId,
      p_subject: subject,
      p_topic_id: String(topicId),
    });
  } catch (_) { /* swallow */ }
}

// Higher-level helpers used by the call sites in lumio.jsx.

// True iff the student has space for one more practice/exam question.
export async function canStartQuestion({ userId, yearId, subject, mode, isAdmin }) {
  if (isAdmin) return { allowed: true, used: 0, limit: Infinity };
  const limit = TRIAL_LIMITS[mode] ?? 0;
  const usage = await getTrialUsage(userId, yearId, subject, mode);
  return { allowed: (usage.count || 0) < limit, used: usage.count || 0, limit };
}

// For learn-mode: true if the topic is already in topics_opened OR the
// student is still under their topic budget.
export async function canOpenTopic({ userId, yearId, subject, topicId, isAdmin }) {
  if (isAdmin) return { allowed: true, alreadyOpened: true };
  const usage = await getTrialUsage(userId, yearId, subject, 'learn');
  const opened = usage.topics_opened || [];
  if (opened.includes(String(topicId))) return { allowed: true, alreadyOpened: true };
  if (opened.length >= TRIAL_LIMITS.learn) return { allowed: false, alreadyOpened: false };
  return { allowed: true, alreadyOpened: false };
}

// Capture upgrade interest (email-only, anonymous-friendly).
export async function captureUpgradeInterest({ userId, email }) {
  if (!supabaseEnabled || !email) return { ok: false };
  try {
    const { error } = await supabase
      .from('upgrade_interest')
      .insert({ user_id: userId || null, email });
    return { ok: !error, error: error?.message };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}
